import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { useMutation } from "@tanstack/react-query";
import { ListCommanWay, PostFormData } from "API/commonApi";
import Tesseract from "tesseract.js";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  setAiReponse: any;
  setAiLoading: any;
  aiLoading: boolean;
  customer_code?: string;
  onLubiResponse?: (data: any[]) => void;
}

// Calculate Laplacian variance (sharpness score) for an image region
const calculateLaplacianVariance = (imageData: ImageData): number => {
  const { data, width, height } = imageData;
  const gray: number[] = [];
  
  // Convert to grayscale
  for (let i = 0; i < data.length; i += 4) {
    gray.push(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
  }
  
  // Apply Laplacian kernel: [0, 1, 0], [1, -4, 1], [0, 1, 0]
  const laplacian: number[] = [];
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = y * width + x;
      const val =
        gray[idx - width] +
        gray[idx - 1] +
        -4 * gray[idx] +
        gray[idx + 1] +
        gray[idx + width];
      laplacian.push(val);
    }
  }
  
  // Calculate variance
  const mean = laplacian.reduce((a, b) => a + b, 0) / laplacian.length;
  const variance = laplacian.reduce((acc, val) => acc + Math.pow(val - mean, 2), 0) / laplacian.length;
  return variance;
};

const MultiImageUploadModal: React.FC<Props> = ({ isOpen, onClose, setAiReponse, setAiLoading, aiLoading, customer_code = "", onLubiResponse }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);

  const [showCamera, setShowCamera] = useState(false);
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [currentDeviceIndex, setCurrentDeviceIndex] = useState(0);

  const getCameras = async () => {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoInputs = devices.filter((device) => device.kind === "videoinput");
    setVideoDevices(videoInputs);
  };

  const [focusSupported, setFocusSupported] = useState(false);
  const [focusDistance, setFocusDistance] = useState(0);
  const [focusMin, setFocusMin] = useState(0);
  const [focusMax, setFocusMax] = useState(1);
  const [currentTrack, setCurrentTrack] = useState<MediaStreamTrack | null>(null);

  // Auto-focus states
  const [autoFocusEnabled, setAutoFocusEnabled] = useState(false);
  const [isAutoFocusing, setIsAutoFocusing] = useState(false);
  const [autoFocusProgress, setAutoFocusProgress] = useState(0);
  const [autoFocusStatus, setAutoFocusStatus] = useState<string>("");
  const [sharpnessScore, setSharpnessScore] = useState(0);
  const [ocrConfidence, setOcrConfidence] = useState(0);
  const autoFocusAbortRef = useRef(false);
  const tesseractWorkerRef = useRef<Tesseract.Worker | null>(null);

  // Initialize Tesseract worker
  const initTesseract = useCallback(async () => {
    if (!tesseractWorkerRef.current) {
      tesseractWorkerRef.current = await Tesseract.createWorker("eng", 1, {
        logger: (m) => {
          if (m.status === "recognizing text") {
            // Optional: track OCR progress
          }
        },
      });
    }
    return tesseractWorkerRef.current;
  }, []);

  // Cleanup Tesseract worker on unmount
  useEffect(() => {
    return () => {
      if (tesseractWorkerRef.current) {
        tesseractWorkerRef.current.terminate();
      }
    };
  }, []);

  // Get center region of video for focus analysis
  const getCenterRegionImageData = (): ImageData | null => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return null;

    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    // Use center 50% of the frame
    const cropWidth = video.videoWidth * 0.5;
    const cropHeight = video.videoHeight * 0.5;
    const startX = (video.videoWidth - cropWidth) / 2;
    const startY = (video.videoHeight - cropHeight) / 2;

    canvas.width = cropWidth;
    canvas.height = cropHeight;
    ctx.drawImage(video, startX, startY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);

    return ctx.getImageData(0, 0, cropWidth, cropHeight);
  };

  // Get center region as data URL for OCR
  const getCenterRegionDataUrl = (): string | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    return canvas.toDataURL("image/jpeg", 0.8);
  };

  // Apply focus distance to camera
  const applyFocus = async (distance: number): Promise<void> => {
    if (!currentTrack) return;
    try {
      await (currentTrack as any).applyConstraints({
        advanced: [{ focusMode: "manual", focusDistance: distance }],
      });
      // Wait for focus to settle
      await new Promise((resolve) => setTimeout(resolve, 150));
    } catch (err) {
      console.error("Focus apply failed:", err);
    }
  };

  // Run OCR on current frame and return confidence
  const runOCR = async (): Promise<{ confidence: number; text: string }> => {
    const dataUrl = getCenterRegionDataUrl();
    if (!dataUrl) return { confidence: 0, text: "" };

    try {
      const worker = await initTesseract();
      const result = await worker.recognize(dataUrl);
      return {
        confidence: result.data.confidence,
        text: result.data.text,
      };
    } catch (err) {
      console.error("OCR failed:", err);
      return { confidence: 0, text: "" };
    }
  };

  // Main auto-focus algorithm
  const runAutoFocus = async () => {
    if (!focusSupported || !currentTrack || isAutoFocusing) return;

    setIsAutoFocusing(true);
    setAutoFocusProgress(0);
    autoFocusAbortRef.current = false;

    try {
      // Phase 1: Coarse scan with Laplacian variance
      setAutoFocusStatus("Scanning for sharpness...");
      const steps = 12;
      const stepSize = (focusMax - focusMin) / steps;
      const candidates: { distance: number; sharpness: number }[] = [];

      for (let i = 0; i <= steps; i++) {
        if (autoFocusAbortRef.current) break;

        const distance = focusMin + i * stepSize;
        await applyFocus(distance);
        setFocusDistance(distance);

        const imageData = getCenterRegionImageData();
        if (imageData) {
          const sharpness = calculateLaplacianVariance(imageData);
          candidates.push({ distance, sharpness });
          setSharpnessScore(Math.round(sharpness));
        }

        setAutoFocusProgress(Math.round((i / steps) * 50));
      }

      if (autoFocusAbortRef.current) {
        setIsAutoFocusing(false);
        setAutoFocusStatus("Cancelled");
        return;
      }

      // Sort by sharpness and get top 5 candidates
      candidates.sort((a, b) => b.sharpness - a.sharpness);
      const topCandidates = candidates.slice(0, 5);

      // Phase 2: Fine-tune with OCR
      setAutoFocusStatus("Analyzing text clarity...");
      let bestFocus = topCandidates[0].distance;
      let bestConfidence = 0;
      let bestText = "";

      for (let i = 0; i < topCandidates.length; i++) {
        if (autoFocusAbortRef.current) break;

        const candidate = topCandidates[i];
        await applyFocus(candidate.distance);
        setFocusDistance(candidate.distance);

        const ocrResult = await runOCR();
        setOcrConfidence(Math.round(ocrResult.confidence));

        if (ocrResult.confidence > bestConfidence) {
          bestConfidence = ocrResult.confidence;
          bestFocus = candidate.distance;
          bestText = ocrResult.text;
        }

        setAutoFocusProgress(50 + Math.round(((i + 1) / topCandidates.length) * 50));
      }

      if (autoFocusAbortRef.current) {
        setIsAutoFocusing(false);
        setAutoFocusStatus("Cancelled");
        return;
      }

      // Apply best focus
      await applyFocus(bestFocus);
      setFocusDistance(bestFocus);
      setAutoFocusProgress(100);

      const hasText = bestText.trim().length > 5;
      if (hasText) {
        setAutoFocusStatus(`Ready! Text detected (${Math.round(bestConfidence)}% confidence)`);
      } else {
        setAutoFocusStatus("Done - No clear text found. Try repositioning.");
      }
    } catch (err) {
      console.error("Auto-focus error:", err);
      setAutoFocusStatus("Error during auto-focus");
    } finally {
      setIsAutoFocusing(false);
    }
  };

  // Cancel auto-focus
  const cancelAutoFocus = () => {
    autoFocusAbortRef.current = true;
  };

  const openWebcam = async (deviceId?: string) => {
    try {
      const constraints = {
        video: deviceId ? { width: { ideal: 1920 }, height: { ideal: 1080 }, deviceId: { exact: deviceId } } : true,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      if (videoStream) {
        videoStream.getTracks().forEach((track) => track.stop());
      }
      stream.getVideoTracks().forEach((track, ii) => {
        const capabilities: any = track.getCapabilities();
        console.log("Focus capabilities:", ii, capabilities);

        if (capabilities.focusDistance) {
          setFocusSupported(true);
          setFocusMin(capabilities.focusDistance.min);
          setFocusMax(capabilities.focusDistance.max);
          setFocusDistance(capabilities.focusDistance.min);
          setCurrentTrack(track);
          
          // Set to manual mode initially
          (track as any).applyConstraints({
            advanced: [{ focusMode: "manual" }],
          }).catch((err: any) => console.error("Focus mode set failed:", err));
        } else {
          setFocusSupported(false);
        }
      });

      // Reset auto-focus states
      setAutoFocusEnabled(false);
      setAutoFocusStatus("");
      setAutoFocusProgress(0);
      setSharpnessScore(0);
      setOcrConfidence(0);

      setVideoStream(stream);
      setShowCamera(true);
    } catch (err) {
      alert("Unable to access the camera");
      console.error(err);
    }
  };

  useEffect(() => {
    if (videoRef.current && videoStream) {
      videoRef.current.srcObject = videoStream;
    }
  }, [videoStream]);

  // Switch between available cameras
  const switchCamera = async () => {
    if (videoDevices.length <= 1) return;

    const nextIndex = (currentDeviceIndex + 1) % videoDevices.length;
    setCurrentDeviceIndex(nextIndex);
    await openWebcam(videoDevices[nextIndex].deviceId);
  };
  // Initialize cameras on component mount
  useEffect(() => {
    getCameras();
  }, []);

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext("2d");
    ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `camera_${Date.now()}.jpg`, { type: "image/jpeg" });
      const url = URL.createObjectURL(file);
      setFiles((prev) => [...prev, file]);
      setPreviewUrls((prev) => [...prev, url]);
      stopCamera();
    }, "image/jpeg");
  };

  const stopCamera = () => {
    videoStream?.getTracks().forEach((track) => track.stop());
    setShowCamera(false);
    setVideoStream(null);
  };

  const handleAddFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const selected = Array.from(e.target.files);
    const newPreviews = selected.map((file) => URL.createObjectURL(file));

    setFiles((prev) => [...prev, ...selected]);
    setPreviewUrls((prev) => [...prev, ...newPreviews]);
  };

  const handleRemove = (index: number) => {
    const updatedFiles = [...files];
    const updatedPreviews = [...previewUrls];
    updatedFiles.splice(index, 1);
    updatedPreviews.splice(index, 1);
    setFiles(updatedFiles);
    setPreviewUrls(updatedPreviews);
  };

  const { mutate }: any = useMutation((data) => PostFormData("/admin/ai/parcel-detect", data), {
    onSuccess: (data: any) => {
      if (data.status === "SUCCESS") {
        console.log(data.data);
        setAiReponse(data?.data || []);
      } else {
        alert("Cannot Load Data");
        setAiLoading(false);
      }
    },
    onError: (data: any) => {
      alert("Cannot Load Data");
      setAiLoading(false);
      //typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  const handleSave = async () => {
    if (aiLoading) {
      return;
    }
    setAiLoading(true);
    if (files.length === 0) return alert("No files selected!");
    onClose();
    const formData = new FormData();

    if (customer_code === "AHD48277") {
      // For AHD48277, call lubi-process instead of parcel-detect
      files.forEach((file) => formData.append("file", file));
      try {
        const res: any = await PostFormData("/admin/customer/lubi-process", formData);
        console.log("Invoice extract response:", res);
        if (res?.status === "SUCCESS" && res?.data?.length && onLubiResponse) {
          onLubiResponse(res.data);
        } else {
          alert("Cannot Load Data");
        }
      } catch (err) {
        console.error("Invoice extract error:", err);
        alert("Cannot Load Data");
      } finally {
        setAiLoading(false);
      }
    } else {
      files.forEach((file) => formData.append("files", file));
      mutate(formData);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center">
      {/* Hidden canvas for image processing */}
      <canvas ref={canvasRef} className="hidden" />

      {showCamera && (
        <div className="absolute z-10 top-0 left-0 w-full h-full bg-black-900/70 flex justify-center items-center">
          <div className="mb-4 relative">
            {/* Video with focus zone overlay */}
            <div className="relative">
              <video ref={videoRef} width={1920} height={1080} autoPlay playsInline muted className="w-full h-[80vh] bg-black rounded" />
              {/* Focus Zone Overlay - center 50% rectangle */}
              {focusSupported && autoFocusEnabled && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div 
                    className={`border-2 ${isAutoFocusing ? 'border-yellow-400 animate-pulse' : 'border-green-400'} rounded-lg`}
                    style={{ width: '50%', height: '50%' }}
                  >
                    <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                      Focus Zone
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Focus Controls */}
            <div className="flex flex-wrap justify-between items-center bg-white mt-1 px-2 py-2 gap-2">
              {focusSupported && (
                <>
                  {/* Auto Focus Toggle */}
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={autoFocusEnabled}
                        onChange={(e) => setAutoFocusEnabled(e.target.checked)}
                        disabled={isAutoFocusing}
                        className="w-4 h-4"
                      />
                      <span className="text-sm font-medium">Text Auto-Focus</span>
                    </label>

                    {autoFocusEnabled && (
                      <>
                        {!isAutoFocusing ? (
                          <button
                            onClick={runAutoFocus}
                            className="bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700"
                          >
                            Start Scan
                          </button>
                        ) : (
                          <button
                            onClick={cancelAutoFocus}
                            className="bg-orange-500 text-white px-3 py-1 rounded text-sm hover:bg-orange-600"
                          >
                            Cancel
                          </button>
                        )}

                        {autoFocusProgress === 100 && !isAutoFocusing && (
                          <button
                            onClick={runAutoFocus}
                            className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600"
                          >
                            Re-scan
                          </button>
                        )}
                      </>
                    )}
                  </div>

                  {/* Manual Focus Slider (shown when auto-focus is off) */}
                  {!autoFocusEnabled && (
                    <div className="flex gap-2 items-center">
                      <label className="text-sm">Manual Focus:</label>
                      <input
                        type="range"
                        min={focusMin}
                        max={focusMax}
                        step="5"
                        className="w-48"
                        value={focusDistance}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value);
                          setFocusDistance(val);
                          if (currentTrack) {
                            (currentTrack as any)
                              .applyConstraints({
                                advanced: [{ focusMode: "manual", focusDistance: val }],
                              })
                              .catch((err: any) => console.error("Focus apply failed:", err));
                          }
                        }}
                      />
                      <span className="text-xs text-gray-500">{Math.round(focusDistance)}</span>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Auto-Focus Progress & Status */}
            {autoFocusEnabled && focusSupported && (
              <div className="bg-gray-100 px-2 py-2">
                {/* Progress Bar */}
                {isAutoFocusing && (
                  <div className="mb-2">
                    <div className="w-full bg-gray-300 rounded-full h-2">
                      <div
                        className="bg-purple-600 h-2 rounded-full transition-all duration-200"
                        style={{ width: `${autoFocusProgress}%` }}
                      />
                    </div>
                    <div className="text-xs text-gray-600 mt-1 text-center">{autoFocusProgress}%</div>
                  </div>
                )}

                {/* Status Message */}
                {autoFocusStatus && (
                  <div className={`text-sm text-center ${autoFocusStatus.includes('Ready') ? 'text-green-600 font-medium' : 'text-gray-700'}`}>
                    {autoFocusStatus}
                  </div>
                )}

                {/* Stats Display */}
                {(sharpnessScore > 0 || ocrConfidence > 0) && (
                  <div className="flex justify-center gap-6 mt-1 text-xs text-gray-500">
                    {sharpnessScore > 0 && <span>Sharpness: {sharpnessScore}</span>}
                    {ocrConfidence > 0 && <span>OCR Confidence: {ocrConfidence}%</span>}
                    <span>Focus: {Math.round(focusDistance)}</span>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2 justify-end mt-2">
              <button onClick={switchCamera} className="bg-blue-500 text-white px-4 py-2 rounded">
                Switch Camera
              </button>
              <button 
                onClick={capturePhoto} 
                disabled={isAutoFocusing}
                className={`px-4 py-1 rounded ${isAutoFocusing ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'} text-white`}
              >
                Capture
              </button>
              <button onClick={stopCamera} className="bg-red-600 text-white px-4 py-1 rounded hover:bg-red-700">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white w-full max-w-2xl p-6 rounded-lg shadow-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Upload Photos</h2>
          <button
            onClick={() => {
              openWebcam();
            }}
            className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 transition text-sm"
          >
            Open Camera
          </button>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-4">
          {previewUrls.map((url, idx) => (
            <div key={idx} className="relative group">
              <img src={url} alt={`form-${idx}`} className="w-full h-28 object-cover rounded border" />
              <button
                className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 text-xs hover:bg-red-700"
                onClick={() => handleRemove(idx)}
              >
                🗑️
              </button>
            </div>
          ))}

          <label className="flex items-center justify-center border-2 border-dashed border-gray-300 rounded w-full h-28 text-3xl text-gray-400 cursor-pointer hover:bg-gray-100 transition">
            +
            <input type="file" multiple accept="image/*,.pdf" className="hidden" onChange={handleAddFiles} />
          </label>
        </div>

        <div className="flex justify-end gap-3">
          <button onClick={handleSave} className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition">
            Save
          </button>
          <button onClick={onClose} className="bg-gray-400 text-white px-4 py-2 rounded hover:bg-gray-500 transition">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default MultiImageUploadModal;
